import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/api';

interface Author {
  firstName?: string;
  lastName?: string;
  teamName: string;
}

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  headerImage?: string;
  publishDate: string;
  status: 'draft' | 'published';
  author: Author;
  createdAt: string;
  updatedAt: string;
}

const NewsWidget: React.FC = () => {
  const [latestNews, setLatestNews] = useState<NewsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLatestNews = async () => {
      // Check if we have cached data with expiration
      const cachedNews = sessionStorage.getItem('latest_news_data');
      const cacheTimestamp = sessionStorage.getItem('latest_news_timestamp');
      
      if (cachedNews && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsed = JSON.parse(cachedNews);
            setLatestNews(parsed);
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('latest_news_data');
            sessionStorage.removeItem('latest_news_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('latest_news_data');
          sessionStorage.removeItem('latest_news_timestamp');
        }
      }
      
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get('/news/latest/preview');
        setLatestNews(response.data);
        
        // Cache the data with timestamp
        sessionStorage.setItem('latest_news_data', JSON.stringify(response.data));
        sessionStorage.setItem('latest_news_timestamp', Date.now().toString());
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to load latest news');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestNews();
  }, []);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatAuthor = (author: Author): string => {
    if (author.firstName && author.lastName) {
      return `${author.firstName} ${author.lastName}`;
    }
    return author.teamName;
  };

  const getExcerpt = (content: string, maxLength: number = 150): string => {
    const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6 w-32"></div>
          <div className="card bg-gray-50 dark:bg-gray-800/50">
            <div className="md:flex">
              <div className="md:w-1/3">
                <div className="aspect-video md:aspect-square w-full bg-gray-300 dark:bg-gray-600"></div>
              </div>
              <div className="p-6 md:w-2/3">
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-3 w-32"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-4 w-3/4"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !latestNews) {
    return null; // Don't show anything if there's an error or no news
  }

  return (
    <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Latest News
        </h2>
      </div>

      <article className="card shadow bg-gray-50 dark:bg-gray-800/50 hover:shadow-lg transition-shadow">
        <div className="md:flex">
          {latestNews.headerImage && (
            <div className="md:w-1/3">
              <div className="aspect-video md:aspect-square w-full">
                <img 
                  src={latestNews.headerImage} 
                  alt={latestNews.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
          
          <div className={`p-6 ${latestNews.headerImage ? 'md:w-2/3' : 'w-full'}`}>
            <div className="mb-4">
              <h3 className="text-2xl font-bold mb-2 hover:text-highlight transition-colors">
                <Link to={`/news/${latestNews.slug}`} style={{ color: 'var(--text-primary)' }}>
                  {latestNews.title}
                </Link>
              </h3>
              <div className="flex items-center text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                <span>{formatDate(latestNews.publishDate)}</span>
              </div>
            </div>
            
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              {getExcerpt(latestNews.content)}
            </p>
            
            <Link 
              to={`/news/${latestNews.slug}`}
              className="inline-flex items-center font-medium transition-colors"
              style={{ color: 'var(--highlight)' }}
            >
              Read more
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </article>
{/* 
      <div className="text-center mt-6 mb-8">
        <Link 
          to="/news"
          className="btn-secondary inline-flex items-center px-4 py-2 text-sm"
        >
          View All News
        </Link>
      </div> */}
    </div>
  );
};

export default NewsWidget;